import { datadogLogs } from "@datadog/browser-logs";
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

import { LOCAL_STORAGE_KEYS } from "src/constants";
import { queryClient } from "src/libs";
import { localStorage } from "src/utils";

interface AuthState {
  token: string | null;
  isOktaLoginEnabled: boolean;
  login: (token: string, isOktaLogin?: boolean) => void;
  logout: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => {
      const login = (token: string, isOktaLogin = false) => {
        set({ token, isOktaLoginEnabled: isOktaLogin });
      };

      const logout = async () => {
        const { isOktaLoginEnabled } = get();

        // If user is logged in with Okta, use Okta's signOut
        if (isOktaLoginEnabled) {
          try {
            const { oktaAuthService } = await import("src/services/oktaAuth.service");
            await oktaAuthService.signOut();
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            throw new Error(`Okta signOut error: ${errorMessage}`);
          }
        }

        localStorage.remove(LOCAL_STORAGE_KEYS.USER_TOKEN);
        set({ token: null, isOktaLoginEnabled: false });
        queryClient.clear();
        datadogLogs.clearUser();
      };

      return {
        token: localStorage.get(LOCAL_STORAGE_KEYS.USER_TOKEN),
        isOktaLoginEnabled: false,
        login,
        logout,
      };
    },
    {
      name: LOCAL_STORAGE_KEYS.AUTH_STORAGE,
      storage: createJSONStorage(() => ({
        setItem: (key: string, value: string) => localStorage.set(key as LOCAL_STORAGE_KEYS, value),
        getItem: (key: string) => localStorage.get(key as LOCAL_STORAGE_KEYS),
        removeItem: (key: string) => localStorage.remove(key as LOCAL_STORAGE_KEYS),
      })),
    }
  )
);
